import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../services/menu/useCart";
import MiniBasketDropdown from "../basket/MiniBasketDropdown";

interface BasketIconProps {
  lang: string;
  size?: number;
  variant?: "first" | "second";
  className?: string;
  noLink?: boolean;
}

const AUTO_CLOSE_MS = 4000;

const BasketIcon: React.FC<BasketIconProps> = ({ lang, size = 32, variant = "second", className, noLink }) => {
  const { cartItems } = useCart();
  const [count, setCount] = useState<number>(cartItems.length);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setCount(cartItems.length);
  }, [cartItems]);

  useEffect(() => {
    const updateCount = () => {
      const cartItemsFromStorage = JSON.parse(localStorage.getItem("cart") || "[]");
      setCount(cartItemsFromStorage.length);
    };

    window.addEventListener("storage", updateCount);
    window.addEventListener("cartUpdated", updateCount);

    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("cartUpdated", updateCount);
    };
  }, []);

  // Mahsulot savatga qo'shilganda mini-savat oynasini avtomatik ochib,
  // bir ozdan keyin o'zi yopiladi — faqat to'liq havola bo'lgan variantda (desktop)
  useEffect(() => {
    if (noLink) return;

    const handleItemAdded = () => {
      setIsOpen(true);
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = setTimeout(() => setIsOpen(false), AUTO_CLOSE_MS);
    };

    window.addEventListener("basket:item-added", handleItemAdded);
    return () => {
      window.removeEventListener("basket:item-added", handleItemAdded);
      clearTimeout(autoCloseTimer.current);
    };
  }, [noLink]);

  // Ochilish animatsiyasi — DOM'ga qo'shilgandan bir zum keyin "ko'rinadigan"
  // holatga o'tkazamiz, shunda transition ishga tushadi
  useEffect(() => {
    if (isOpen) {
      const frame = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setIsVisible(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const fillColor = variant === "first" ? "white" : "black";

  const iconContent = (
    <>
      <svg width={size} height={(size * 29) / 27} viewBox="0 0 27 29" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.0896 0V5.27273H9.40298L14.7761 10.5455L20.1493 5.27273H17.4627V0M0 2.63636V5.27273H2.68657L7.52239 15.2909L5.64179 18.4545C5.50746 18.85 5.37313 19.2455 5.37313 19.7727C5.37313 21.2227 6.58209 22.4091 8.0597 22.4091H24.1791V19.7727H8.59701C8.46269 19.7727 8.32836 19.6409 8.32836 19.5091V19.3773L9.53731 17.1364H19.4776C20.4179 17.1364 21.3582 16.6091 21.7612 15.8182L27 6.59091L24.7164 5.27273L19.4776 14.5H10.0746L4.43284 2.63636M8.0597 23.7273C6.58209 23.7273 5.37313 24.9136 5.37313 26.3636C5.37313 27.8136 6.58209 29 8.0597 29C9.53731 29 10.7463 27.8136 10.7463 26.3636C10.7463 24.9136 9.53731 23.7273 8.0597 23.7273ZM21.4925 23.7273C20.0149 23.7273 18.806 24.9136 18.806 26.3636C18.806 27.8136 20.0149 29 21.4925 29C22.9701 29 24.1791 27.8136 24.1791 26.3636C24.1791 24.9136 22.9701 23.7273 21.4925 23.7273Z" fill={fillColor}/>
      </svg>
      {count > 0 && (
        <span className="absolute -top-2 -right-3 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ minWidth: "15px", textAlign: "center" }}>
          {count}
        </span>
      )}
    </>
  );

  if (noLink) {
    return (
      <div className={`relative ${className || ""}`}>
        {iconContent}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Link
        to={`/${lang}/basket`}
        className={`relative ${className || ""}`}
        title={lang === "uz" ? "Savatcha" : "Корзина"}
      >
        {iconContent}
      </Link>

      {isOpen && (
        <div
          className={`absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2 origin-top transition-all duration-200 ease-out ${
            isVisible
              ? "translate-y-0 scale-100 opacity-100"
              : "-translate-y-1 scale-95 opacity-0"
          }`}
        >
          <MiniBasketDropdown
            lang={lang}
            cartItems={cartItems}
            onClose={() => setIsOpen(false)}
          />
        </div>
      )}
    </div>
  );
};


export default BasketIcon;
